'use client'

import { IRedux } from '@/app/[locale]/_store/types'
import {
  Box,
  Button,
  Dialog,
  HStack,
  Portal,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { FC, useState, useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  activatePoint,
  onOpenMap,
  setDeliveryZone,
} from '@/app/[locale]/_store/common/common.slice'
import { useTranslations } from 'next-intl'
import { toaster } from '@/components/ui/toaster'
import { getCheckPointDeliveryZones } from '@/app/[locale]/_service/zone.service'
import { IPoint } from '@/app/[locale]/_store/common/types'
import { IProps } from '../_types'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'
import { RadioCardItem, RadioCardRoot } from '@/components/ui/radio-card'
import { CheckboxIcon, PlusIcon } from '@/app/[locale]/_assets/icon-components'
import { CloseButton } from '@/components/ui/close-button'

const Desktop: FC<IProps> = ({ isOpen, onClose, isModal = false }) => {
  const t = useTranslations()
  const dispatch = useDispatch()
  const { points } = useSelector((state: IRedux) => state.common, shallowEqual)
  const { useDynamicLanguage: dynamicLanguageEnabled } = useDynamicLanguage()

  const [value, setValue] = useState<string>('')

  const handleTariffGeozone = useCallback(
    async (lat: number, lon: number, point: IPoint) => {
      try {
        const data = await getCheckPointDeliveryZones(
          { lat, lon },
          dynamicLanguageEnabled
        )
        if (data?.data?.all_count > 0 && !data?.data?.result?.length) {
          toaster.create({
            title: t('address_not_in_service_area'),
            type: 'error',
          })
        } else {
          const currentZone = data?.data?.result?.[0]
          if (currentZone?.id) {
            dispatch(
              setDeliveryZone({
                id: currentZone.id,
                min_order_amount: currentZone.min_order_amount,
              })
            )
          }
          dispatch(activatePoint(point.id))
          onClose()
        }
      } catch (error) {
        console.error(error)
      }
    },
    [dispatch, t]
  )

  const handleSelected = useCallback(
    (point: IPoint) => {
      setValue(point.id)
      if (point?.location?.length === 2) {
        const [lat, lon] = point.location
        handleTariffGeozone(lat, lon, point)
      }
    },
    [handleTariffGeozone]
  )

  const handleClick = () => {
    dispatch(onOpenMap())
    onClose()
  }

  const activeAddress = points?.find((el) => el?.isActive)

  if (points?.length > 0) {
    if (isModal) {
      return (
        <Dialog.Root lazyMount open={isOpen} onOpenChange={onClose}>
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content
                bg={'background.card'}
                color={'text'}
                p={4}
                rounded={12}
              >
                <Dialog.Header
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  p={0}
                  pb={3}
                >
                  <Dialog.Title>
                    <Text fontSize='xl' fontWeight='bold'>
                      {t('addresses')}
                    </Text>
                  </Dialog.Title>
                  <Dialog.CloseTrigger asChild>
                    <CloseButton size='sm' />
                  </Dialog.CloseTrigger>
                </Dialog.Header>
                <Dialog.Body p={0}>
                  <VStack
                    display={{ base: 'none', lg: 'flex' }}
                    gap={4}
                    w='full'
                  >
                    <RadioCardRoot
                      unstyled
                      w='full'
                      value={value}
                      onValueChange={(e: any) => setValue(e.value)}
                    >
                      <Stack w='full' gap={2}>
                        {points?.map(
                          (point) =>
                            point && (
                              <HStack
                                key={point.id}
                                px={4}
                                py={3}
                                w='full'
                                rounded={16}
                                bg='background'
                                cursor='pointer'
                                justifyContent='space-between'
                                fontSize={{ base: 'sm', lg: 'md' }}
                                onClick={() => handleSelected(point)}
                              >
                                <Text
                                  truncate
                                  maxW={'450px'}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {point.address}
                                </Text>
                                <RadioCardItem
                                  cursor='pointer'
                                  value={point.id}
                                  id={point.id}
                                  indicator={false}
                                  border='none'
                                  icon={
                                    activeAddress?.id === point.id ? (
                                      <CheckboxIcon color='var(--primary-default)' />
                                    ) : (
                                      <Box
                                        bg='light'
                                        w={7}
                                        h={7}
                                        rounded='full'
                                      />
                                    )
                                  }
                                />
                              </HStack>
                            )
                        )}
                      </Stack>
                    </RadioCardRoot>
                    <Button
                      w='full'
                      h='48px'
                      rounded={16}
                      variant='plain'
                      color='primary'
                      border='1px solid'
                      borderColor='primary'
                      _hover={{ bgColor: 'primary', color: 'text' }}
                      size='lg'
                      onClick={handleClick}
                    >
                      <PlusIcon color='currentColor' /> {t('add_new_address')}
                    </Button>
                  </VStack>
                </Dialog.Body>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      )
    } else {
      return (
        <VStack display={{ base: 'none', lg: 'flex' }} gap={4} w='full'>
          <RadioCardRoot
            unstyled
            w='full'
            value={value}
            onValueChange={(e: any) => setValue(e.value)}
          >
            <Stack w='full' gap={2}>
              {points?.map(
                (point) =>
                  point && (
                    <HStack
                      key={point.id}
                      px={4}
                      py={3}
                      w='full'
                      rounded={16}
                      bg='background'
                      cursor='pointer'
                      justifyContent='space-between'
                      fontSize={{ base: 'sm', lg: 'md' }}
                      onClick={() => handleSelected(point)}
                    >
                      <label htmlFor={point.id} style={{ cursor: 'pointer' }}>
                        {point.address}
                      </label>
                      <RadioCardItem
                        cursor='pointer'
                        value={point.id}
                        id={point.id}
                        indicator={false}
                        border='none'
                        icon={
                          activeAddress?.id === point.id ? (
                            <CheckboxIcon color='var(--primary-default)' />
                          ) : (
                            <Box bg='light' w={7} h={7} rounded='full' />
                          )
                        }
                      />
                    </HStack>
                  )
              )}
            </Stack>
          </RadioCardRoot>
          <Button
            w='full'
            h='48px'
            rounded={16}
            variant='plain'
            color='primary'
            border='1px solid'
            borderColor='primary'
            _hover={{ bgColor: 'primary', color: 'light' }}
            size='lg'
            onClick={handleClick}
          >
            <PlusIcon color='currentColor' /> {t('add_new_address')}
          </Button>
        </VStack>
      )
    }
  }

  return null
}

export default Desktop
